import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';

import styles from './index.module.scss';

import ConfirmSlider from '@/components/ui/ConfirmSlider';
import Spinner from '@/components/ui/Spinner';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { sleep } from '@/utils/Sleep';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// 상수 정의
const MOTOR_NUMBERS = [1, 2, 3, 4, 5];
const MOTOR_THROTTLE_PERCENTAGE = 10;
const SURFACE_NUMBERS = [6, 7, 8, 9];
const SURFACE_PWM_MIN = 1100;
const SURFACE_PWM_MAX = 1900;
const TIMEOUT = 3;

const Panel = ({ data: robots }) => {
  const [isProcessing, setProcessing] = useState(false);
  const [currentTest, setCurrentTest] = useState();
  const surfaceDefaultTrim = useRef({});
  const testTarget = useRef();

  const actionCommand = useActionCommand();

  useMountEffect(() => {
    robots.forEach((robot) => {
      surfaceDefaultTrim.current[robot.id] = {
        SERVO6_TRIM: Number(robot.metadata['SERVO6_DEFAULT_TRIM']),
        SERVO7_TRIM: Number(robot.metadata['SERVO7_DEFAULT_TRIM']),
        SERVO8_TRIM: Number(robot.metadata['SERVO8_DEFAULT_TRIM']),
        SERVO9_TRIM: Number(robot.metadata['SERVO9_DEFAULT_TRIM']),
      };
    });
  });

  //#region Process Functions
  const process = async (robot) => {
    try {
      // 전체 테스트
      if (testTarget.current === 'all') {
        // Motor #1: 우측 전방
        setCurrentTest({ type: 'motor', number: 1 });
        await processMotor(robot, 1);

        // Motor #2: 좌측 후방
        setCurrentTest({ type: 'motor', number: 2 });
        await processMotor(robot, 2);

        // Motor #3: 좌측 전방
        setCurrentTest({ type: 'motor', number: 3 });
        await processMotor(robot, 3);

        // Motor #4: 우측 후방
        setCurrentTest({ type: 'motor', number: 4 });
        await processMotor(robot, 4);

        // Motor #5: 중앙 후방
        setCurrentTest({ type: 'motor', number: 5 });
        await processMotor(robot, 5);

        // 주익 좌측 타면
        setCurrentTest({ type: 'surface', number: 6 });
        await processSurface(robot, 6);

        // 주익 우측 타면
        setCurrentTest({ type: 'surface', number: 7 });
        await processSurface(robot, 7);

        // 미익 좌측 타면
        setCurrentTest({ type: 'surface', number: 8 });
        await processSurface(robot, 8);

        // 미익 우측 타면
        setCurrentTest({ type: 'surface', number: 9 });
        await processSurface(robot, 9);
      }
      // 개별 테스트
      else {
        setCurrentTest(testTarget.current);

        // 모터 테스트
        if (testTarget.current.type === 'motor') {
          await processMotor(robot, testTarget.current.number);
        }
        // 타면 테스트
        else if (testTarget.current.type === 'surface') {
          await processSurface(robot, testTarget.current.number);
        }
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processMotor = async (robot, motorNumber) => {
    await executeMavLinkCommand(robot, actionCommand.motorTest(motorNumber, 0, MOTOR_THROTTLE_PERCENTAGE, TIMEOUT));
    await sleep(TIMEOUT * 1000 + 1000); // 모터 테스트 실행 후 대기
  };

  const processSurface = async (robot, instance) => {
    await executeMavLinkCommand(robot, actionCommand.setServo(instance, SURFACE_PWM_MIN));
    await sleep(1000);
    await executeMavLinkCommand(robot, actionCommand.setServo(instance, SURFACE_PWM_MAX));
    await sleep(1000);
    await executeMavLinkCommand(
      robot,
      actionCommand.setServo(instance, surfaceDefaultTrim.current[robot.id][`SERVO${instance}_TRIM`])
    );
    await sleep(1000);
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.success(`Inspection completed successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.error(`Inspection failed on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doStartAll = () => {
    testTarget.current = 'all';
    setProcessing(true);
  };

  const doStartSingle = (e) => {
    const type = e.currentTarget.dataset.type;
    const number = Number(e.currentTarget.dataset[type]);

    testTarget.current = { type, number };
    setProcessing(true);
  };

  const doReset = (e) => {
    const surface = Number(e.currentTarget.dataset.surface);

    robots.forEach((robot) => {
      executeMavLinkCommand(
        robot,
        actionCommand.setServo(surface, surfaceDefaultTrim.current[robot.id][`SERVO${surface}_TRIM`])
      );
    });
  };

  const getActive = (type, number) => {
    return currentTest?.type === type && currentTest?.number === number;
  };

  return (
    <div className={cx('container')}>
      <div className={cx('figure')}>
        <img src={require('./inspection-body.png')} alt="" className={cx('body')} />
        {/* Motor #1 */}
        <div className={cx('motor', 'motor1')}>
          <img
            src={require('./inspection-propeller-counter-clockwise.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive('motor', MOTOR_NUMBERS[0]) }])}
          />
          <div className={cx(['info', 'row'])}>
            <svg width="48" height="24">
              <line
                x1="0"
                y1="0"
                x2="48"
                y2="24"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #1</div>
              <button data-motor={1} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[0]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[0]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #2 */}
        <div className={cx('motor', 'motor2')}>
          <img
            src={require('./inspection-propeller-clockwise.png')}
            alt=""
            className={cx(['propeller', { active: getActive('motor', MOTOR_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="72" height="22">
              <line
                x1="72"
                y1="0"
                x2="36"
                y2="22"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #2</div>
              <button data-motor={2} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[1]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[1]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #3 */}
        <div className={cx('motor', 'motor3')}>
          <img
            src={require('./inspection-propeller-clockwise.png')}
            alt=""
            className={cx(['propeller', { active: getActive('motor', MOTOR_NUMBERS[2]) }])}
          />
          <div className={cx(['info', 'row'])}>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #3</div>
              <button data-motor={3} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[2]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[2]) && <Spinner size={14} />}
              </button>
            </div>
            <svg width="48" height="24">
              <line
                x1="0"
                y1="24"
                x2="48"
                y2="0"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
          </div>
        </div>
        {/* Motor #4 */}
        <div className={cx('motor', 'motor4')}>
          <img
            src={require('./inspection-propeller-counter-clockwise.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive('motor', MOTOR_NUMBERS[3]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="22">
              <line
                x1="0"
                y1="0"
                x2="36"
                y2="22"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #4</div>
              <button data-motor={4} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[3]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[3]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #5 */}
        <div className={cx('motor', 'motor5')}>
          <img
            src={require('./inspection-propeller-rear.png')}
            alt=""
            className={cx(['propeller', 'rear', { active: getActive('motor', MOTOR_NUMBERS[4]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="24">
              <line
                x1="36"
                y1="0"
                x2="36"
                y2="24"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #5</div>
              <button data-motor={5} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('motor', MOTOR_NUMBERS[4]) && 'Start'}
                {getActive('motor', MOTOR_NUMBERS[4]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Wing Left */}
        <div className={cx('wing', 'left')}>
          <img
            src={require('./inspection-surface-left.png')}
            alt=""
            className={cx(['surface', { active: getActive('surface', SURFACE_NUMBERS[0]) }])}
          />
          <div className={cx('info')}>
            <svg width="72" height="80">
              <line
                x1="72"
                y1="0"
                x2="36"
                y2="80"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (L)</div>
              <button data-surface={6} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[0]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[0]) && <Spinner size={14} />}
              </button>
              <button data-surface={6} onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
        {/* Wing Right */}
        <div className={cx('wing', 'right')}>
          <img
            src={require('./inspection-surface-right.png')}
            alt=""
            className={cx(['surface', 'right', { active: getActive('surface', SURFACE_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="80">
              <line
                x1="0"
                y1="0"
                x2="36"
                y2="80"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (R)</div>
              <button data-surface={7} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[1]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[1]) && <Spinner size={14} />}
              </button>
              <button data-surface={7} onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
        {/* Tail Left */}
        <div className={cx('tail', 'left')}>
          <img
            src={require('./inspection-surface-tail-left.png')}
            alt=""
            className={cx(['surface', { active: getActive('surface', SURFACE_NUMBERS[2]) }])}
          />
          <div className={cx(['info', 'left'])}>
            <svg width="24" height="32">
              <line
                x1="24"
                y1="0"
                x2="0"
                y2="32"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (L)</div>
              <button data-surface={8} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[2]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[2]) && <Spinner size={14} />}
              </button>
              <button data-surface={8} onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
        {/* Tail Right */}
        <div className={cx('tail', 'right')}>
          <img
            src={require('./inspection-surface-tail-right.png')}
            alt=""
            className={cx(['surface', 'right', { active: getActive('surface', SURFACE_NUMBERS[3]) }])}
          />
          <div className={cx('info')}>
            <svg width="24" height="32">
              <line
                x1="0"
                y1="0"
                x2="24"
                y2="32"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (R)</div>
              <button data-surface={9} data-type="surface" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive('surface', SURFACE_NUMBERS[3]) && 'Start'}
                {getActive('surface', SURFACE_NUMBERS[3]) && <Spinner size={14} />}
              </button>
              <button data-surface={9} onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmSlider onConfirm={doStartAll} isProcessing={isProcessing} />
    </div>
  );
};

export default Panel;
